'use client';

import React, { useState } from 'react';
import { Bell, Search, ChevronDown, Plus, X } from 'lucide-react';

const notifications = [
  { id: 'notif-1', type: 'alert', title: 'Campaign "Summer Sale" CTR dropped below 0.8%', time: '8m ago', read: false },
  { id: 'notif-2', type: 'success', title: 'Post scheduled for Bravo Coffee published successfully', time: '22m ago', read: false },
  { id: 'notif-3', type: 'alert', title: 'Ad budget 90% consumed — "Q3 Launch" for Nexus Tech', time: '1h ago', read: false },
  { id: 'notif-4', type: 'info', title: 'Team member Priya Nair accepted your invitation', time: '2h ago', read: true },
  { id: 'notif-5', type: 'success', title: '"Autumn Collection" campaign approved and live', time: '3h ago', read: true },
];

interface TopbarProps {
  sidebarCollapsed: boolean;
}

export default function Topbar({ sidebarCollapsed }: TopbarProps) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifList, setNotifList] = useState(notifications);

  const unreadCount = notifList.filter((n) => !n.read).length;

  const markAllRead = () => {
    setNotifList((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
    <header
      className={`
        fixed top-0 right-0 z-20 h-16 bg-card border-b border-border flex items-center px-4 gap-4
        transition-all duration-300
        ${sidebarCollapsed ? 'left-16' : 'left-60'}
      `}
    >
      {/* Search */}
      <div className="relative flex-1 max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search campaigns, clients, posts…"
          className="w-full pl-9 pr-3 py-2 rounded-lg bg-muted border-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 transition-all"
        />
        <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground bg-background border border-border rounded px-1.5 py-0.5 hidden lg:flex">⌘K</kbd>
      </div>

      <div className="flex-1" />

      {/* Client Switcher */}
      <button className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background hover:bg-muted text-sm font-medium transition-all duration-150">
        <div className="w-5 h-5 rounded bg-primary/10 flex items-center justify-center">
          <span className="text-[9px] font-bold text-primary">A</span>
        </div>
        <span className="text-foreground">All Clients</span>
        <ChevronDown size={14} className="text-muted-foreground" />
      </button>

      {/* Quick Create */}
      <button className="btn-primary text-xs px-3 py-2 hidden sm:flex">
        <Plus size={14} />
        <span>New Campaign</span>
      </button>

      {/* Notifications */}
      <div className="relative">
        <button
          onClick={() => setShowNotifications((v) => !v)}
          className="relative w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
        >
          <Bell size={18} />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-negative" />
          )}
        </button>

        {showNotifications && (
          <div className="absolute right-0 top-12 w-80 bg-card border border-border rounded-xl shadow-modal z-50 fade-in">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">Notifications</span>
                {unreadCount > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={markAllRead} className="text-xs text-primary hover:underline">Mark all read</button>
                <button onClick={() => setShowNotifications(false)} className="text-muted-foreground hover:text-foreground">
                  <X size={14} />
                </button>
              </div>
            </div>
            <ul className="divide-y divide-border max-h-72 overflow-y-auto scrollbar-thin">
              {notifList.map((n) => (
                <li key={n.id} className={`px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer ${!n.read ? 'bg-primary/[0.03]' : ''}`}>
                  <div className="flex items-start gap-2">
                    <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${!n.read ? 'bg-primary' : 'bg-transparent'}`} />
                    <div>
                      <p className={`text-xs leading-relaxed ${!n.read ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                        {n.title}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{n.time}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="px-4 py-2 border-t border-border">
              <button className="text-xs text-primary hover:underline w-full text-center">View all notifications</button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}